import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/utils';

import { useAnchorable } from '../../hooks/useAnchorable';

import {
  bottomContainer,
  description,
  list,
  listContainer,
  listDefault,
  listInline,
  question,
  topContainer,
} from './Poll.css';
import { type PollChoiceType, PollStyle, type TPostPollProps } from './Poll.types';
import { PollChoice } from './PollChoice';
import { type PollResultsData, usePollInteraction } from './PollInteractionContext';
import { PollResults } from './PollResults';

const getListStyleType = (pollStyle?: string) => {
  if (pollStyle === 'bullet_points') return 'disc';
  if (pollStyle === 'numbered_list') return 'decimal';
  return 'none';
};

type PollState =
  | { status: 'unvoted' }
  | { status: 'loading'; selectedChoiceId: string }
  | { status: 'results'; selectedChoiceId: string; resultsData: PollResultsData; feedbackUrl?: string }
  | { status: 'error' };

export const PostPoll = ({
  element,
  loginPath,
  subscribePath,
  isSubscriberAuthenticated,
  translations,
}: TPostPollProps) => {
  const { anchorAttributes } = useAnchorable(element.attrs);
  const pollInteraction = usePollInteraction();
  const [pollState, setPollState] = useState<PollState>({ status: 'unvoted' });
  const isSubmittingRef = useRef(false);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(errorTimeoutRef.current), []);

  const { data: poll } = element.attrs;

  const { question: questionText, description: descriptionText } = poll || {};
  const pollStyle = poll?.poll_style || PollStyle.DEFAULT;

  const isInteractive = !!pollInteraction && isSubscriberAuthenticated === true && !!poll?.poll_id;

  const handleVote = useCallback(
    async (choice: PollChoiceType) => {
      if (!pollInteraction || !poll?.poll_id || isSubmittingRef.current) return;

      // Parse ppid and sid from response_url query string
      let postPollId = '';
      let subscriberId = '';
      let parsedUrl: URL;
      try {
        parsedUrl = new URL(choice.response_url, window.location.origin);
      } catch {
        return;
      }

      if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') return;

      postPollId = parsedUrl.searchParams.get('ppid') || '';
      subscriberId = parsedUrl.searchParams.get('sid') || '';

      if (!postPollId || !subscriberId) {
        // Missing required params — fall back to standard navigation
        window.location.href = parsedUrl.href;
        return;
      }

      isSubmittingRef.current = true;
      setPollState({ status: 'loading', selectedChoiceId: choice.id });

      try {
        const results = await pollInteraction.submitVote({
          pollId: poll.poll_id,
          pollChoiceId: choice.id,
          postPollId,
          subscriberId,
        });

        if (results) {
          const feedbackUrl =
            poll.extended_feedback_enabled && poll.poll_id
              ? `/polls/${poll.poll_id}/results?pcid=${choice.id}&ppid=${postPollId}&sid=${subscriberId}`
              : undefined;
          setPollState({ status: 'results', selectedChoiceId: choice.id, resultsData: results, feedbackUrl });
        } else {
          setPollState({ status: 'error' });
          errorTimeoutRef.current = setTimeout(() => setPollState({ status: 'unvoted' }), 3000);
        }
      } catch {
        setPollState({ status: 'error' });
        errorTimeoutRef.current = setTimeout(() => setPollState({ status: 'unvoted' }), 3000);
      } finally {
        isSubmittingRef.current = false;
      }
    },
    [pollInteraction, poll],
  );

  if (!poll) {
    return null;
  }

  const containerStyle = {
    '--list-style-type': getListStyleType(pollStyle),
    ...(['default', 'inline'].includes(pollStyle) ? { '--list-margin-left': '0px' } : {}),
  } as CSSProperties;

  // Show results inline
  if (pollState.status === 'results') {
    return (
      <div {...anchorAttributes} style={containerStyle}>
        <div>
          <div className={topContainer}>
            {questionText && <h3 className={question}>{questionText}</h3>}
            {descriptionText && <div className={description}>{descriptionText}</div>}
          </div>
          <PollResults
            poll={poll}
            resultsData={pollState.resultsData}
            selectedChoiceId={pollState.selectedChoiceId}
            translations={translations}
            feedbackUrl={pollState.feedbackUrl}
          />
        </div>
      </div>
    );
  }

  // Show error state
  if (pollState.status === 'error') {
    return (
      <div {...anchorAttributes} style={containerStyle}>
        <div>
          <div className={topContainer}>
            {questionText && <h3 className={question}>{questionText}</h3>}
            {descriptionText && <div className={description}>{descriptionText}</div>}
          </div>
          <div className={bottomContainer}>
            <p style={{ color: 'var(--post-section-text-color, var(--site-theme-text-on-background-color, inherit))' }}>
              {translations?.['polls.inline.error'] || 'Something went wrong. Please try again.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...anchorAttributes} style={containerStyle}>
      <div>
        <div className={topContainer}>
          {questionText && <h3 className={question}>{questionText}</h3>}
          {descriptionText && <div className={description}>{descriptionText}</div>}
        </div>
        <div className={listContainer}>
          <ul className={cn(list, pollStyle === 'default' && listDefault, pollStyle === 'inline' && listInline)}>
            {poll?.poll_choices?.map((choice, index) => (
              <PollChoice
                choice={choice}
                key={`${choice.id}-${choice.response_url}`}
                pollStyle={pollStyle}
                isLast={index === poll?.poll_choices?.length - 1}
                isInteractive={isInteractive}
                isLoading={pollState.status === 'loading' && pollState.selectedChoiceId === choice.id}
                isDisabled={pollState.status === 'loading'}
                onVote={() => handleVote(choice)}
              />
            ))}
          </ul>
        </div>
      </div>
      {isSubscriberAuthenticated !== true && (
        <div className={bottomContainer}>
          <p style={{ color: 'var(--post-section-text-color, --wt-text-on-background)' }}>
            <span>
              <a href={loginPath} className="underline">
                {translations?.['shared.cta.login']}
              </a>
            </span>{' '}
            {translations?.['shared.cta.or']}{' '}
            <span>
              <a href={subscribePath} className="underline">
                {translations?.['shared.cta.subscribe']}
              </a>
            </span>{' '}
            {translations?.['shared.cta.to_participate']}
          </p>
        </div>
      )}
    </div>
  );
};
